import { useRef } from "react";
import { useLocation } from "react-router-dom";
import { apiClient } from "../../../services/api";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { DebugLogger } from "../utils/debugLogger";
import { useEffectOnce } from "./useEffectOnce";

const debug = new DebugLogger("Email Click");
debug.mute();

/**
 * Reports that this visit came from one of our emails.
 *
 * A ROUTER-LEVEL concern, and a hook rather than a component for the reason
 * `apps/webapp/AGENTS.md` gives: raw `useEffect`/`useRef` are banned in
 * business components, and a side effect that fires on arrival belongs in a
 * dedicated hook beside `useRouterPageView` — which is also where it is
 * called, so it runs above any specific Route.
 *
 * It replaces Resend's click tracking, which rewrote every href onto a
 * tracking subdomain absent from the app's `apple-app-site-association`, so
 * every email CTA opened the mobile web app instead of the native one. Our
 * links stay on `app.juisci.com` — whose AASA claims `/*` — and the click is
 * reported from here instead of by a redirect hop nobody can make open the app.
 *
 * The UTMs are deliberately NOT stripped from the URL, unlike a one-shot
 * deep-link flag: GA4 reads them off the page view, so removing them would
 * defeat the attribution this exists to enable. They are safe to leave — a
 * workflow name, a step id and a date grant nothing, which is why the design
 * carries no token (`.knowledge/mixpanel-capability-url-leak.md`).
 */
export function useReportEmailClick(): void {
  const location = useLocation();
  const { currentUser } = useCurrentUser();
  const params = new URLSearchParams(location.search);
  const utmSource = params.get("utm_source");
  const utmMedium = params.get("utm_medium");
  const utmContent = params.get("utm_content");
  const utmTerm = params.get("utm_term");

  // Latched on SUCCESS, not on attempt: a failed report would otherwise be
  // lost for the life of the session with nothing to show it. Keyed by the
  // campaign so one arrival reports once, however often this re-runs.
  const reported = useRef<string | null>(null);

  useEffectOnce(() => {
    // The endpoint is authenticated — it finds the send from the caller's own
    // session — so a click that lands before the session resolves waits for it
    // rather than being dropped.
    if (currentUser === null) return;
    if (utmMedium !== "email") return;
    if (utmSource === null || utmContent === null || utmTerm === null) return;

    const key = `${utmSource}:${utmContent}:${utmTerm}`;
    if (reported.current === key) return;

    void apiClient
      .post("/webapp/private/email-click", {
        json: { utmSource, utmMedium, utmContent, utmTerm },
      })
      .then(() => {
        reported.current = key;
        debug.log("Reported", { key });
      })
      .catch((error: unknown) => {
        // Logged, never swallowed. Losing a click must not cost the reader the
        // page they asked for, so it is not surfaced to them — but it stays
        // visible here, and the next navigation carrying the same campaign
        // retries because the key was never latched.
        debug.error("Report failed", { key, error });
      });
  }, [currentUser, location.search]);
}
